import type { ReactNode } from 'react';
import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  DollarOutlined,
  WarningOutlined,
  GiftOutlined,
  TeamOutlined,
  LineChartOutlined,
  HistoryOutlined,
  BugOutlined,
  CrownOutlined,
  BarChartOutlined,
  FundOutlined,
  RadarChartOutlined,
  HeatMapOutlined,
  FunnelPlotOutlined,
  AlertOutlined,
  RobotOutlined,
  ExperimentOutlined,
  ApiOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';

/**
 * Single source of truth for the admin-portal left navigation.
 *
 * Canonical rule: every admin destination is a leaf nested under its domain
 * group (max 2 levels: group -> leaf). Standalone leaves (Dashboard) may sit at
 * the top level. In-content tabs are reserved for different VIEWS of the same
 * record — never for navigating between admin destinations.
 *
 * Adding a page = append one NavLeaf to the relevant group's `children`.
 */

export interface NavLeaf {
  /** Real route path registered in App.tsx. Navigated to on click. */
  path: string;
  name: string;
  icon: ReactNode;
  /** Render only when claims.adminLevel === 'super'. */
  requiresSuperAdmin?: boolean;
}

export interface NavGroup {
  /** Stable key used to derive a synthetic, non-navigable menu path. */
  key: string;
  name: string;
  icon: ReactNode;
  children: NavLeaf[];
}

export type NavEntry = NavLeaf | NavGroup;

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return (entry as NavGroup).children !== undefined;
}

export const navigation: NavEntry[] = [
  { path: '/dashboard', name: 'Dashboard', icon: <DashboardOutlined /> },
  {
    key: 'business',
    name: 'Business',
    icon: <DollarOutlined />,
    children: [
      { path: '/users', name: 'Users', icon: <UserOutlined /> },
      { path: '/subscription-plans', name: 'Subscription Plans', icon: <CrownOutlined /> },
      { path: '/revenue', name: 'Revenue', icon: <DollarOutlined /> },
      { path: '/payments', name: 'Payment Failures', icon: <WarningOutlined /> },
      { path: '/discounts', name: 'Discount Codes', icon: <GiftOutlined /> },
      { path: '/referrals', name: 'Referrals', icon: <TeamOutlined /> },
      {
        path: '/marketing-performance',
        name: 'Marketing Performance',
        icon: <ExperimentOutlined />,
        requiresSuperAdmin: true,
      },
    ],
  },
  {
    key: 'signals',
    name: 'Signals',
    icon: <LineChartOutlined />,
    children: [
      { path: '/signals', name: 'Signal Analytics', icon: <LineChartOutlined /> },
      { path: '/signals/promotion-readiness', name: 'Promotion Readiness', icon: <ExperimentOutlined /> },
      { path: '/signals/expiration-policy', name: 'Expiration Policy', icon: <ClockCircleOutlined /> },
      { path: '/signals/ai-quality-gates', name: 'AI Quality Gates', icon: <RobotOutlined /> },
    ],
  },
  {
    key: 'analytics',
    name: 'Analytics',
    icon: <BarChartOutlined />,
    children: [
      { path: '/analytics/cohort-retention', name: 'Cohort & Retention', icon: <HeatMapOutlined /> },
      { path: '/analytics/revenue-deep-dive', name: 'Revenue Deep-Dive', icon: <FundOutlined /> },
      { path: '/analytics/signal-performance', name: 'Signal Performance', icon: <LineChartOutlined /> },
      { path: '/analytics/user-behavior', name: 'User Behavior', icon: <RadarChartOutlined /> },
      { path: '/analytics/funnel', name: 'Funnel & Conversion', icon: <FunnelPlotOutlined /> },
    ],
  },
  {
    key: 'brokers',
    name: 'Brokers',
    icon: <ApiOutlined />,
    children: [
      { path: '/admin/brokers', name: 'Overview', icon: <DashboardOutlined /> },
      { path: '/admin/brokers/cohorts', name: 'Cohorts', icon: <TeamOutlined /> },
      { path: '/admin/brokers/providers', name: 'Providers', icon: <ApiOutlined /> },
      { path: '/admin/brokers/plan-features', name: 'Plan-Feature Mapping', icon: <CrownOutlined /> },
      { path: '/admin/brokers/webhook-ops', name: 'Webhook Ops', icon: <AlertOutlined /> },
      { path: '/admin/brokers/user-ops', name: 'User Ops', icon: <UserOutlined /> },
      { path: '/admin/brokers/broker-availability', name: 'Broker Availability', icon: <ExperimentOutlined /> },
      { path: '/admin/brokers/audit', name: 'Audit Log', icon: <HistoryOutlined /> },
    ],
  },
  {
    key: 'operations',
    name: 'Operations',
    icon: <SettingOutlined />,
    children: [
      { path: '/alerts', name: 'Alerts', icon: <AlertOutlined /> },
      { path: '/settings', name: 'Settings', icon: <SettingOutlined /> },
      { path: '/activity-logs', name: 'Activity Logs', icon: <HistoryOutlined /> },
      { path: '/bug-reports', name: 'Bug Reports', icon: <BugOutlined /> },
      {
        path: '/ai-config',
        name: 'AI Configuration',
        icon: <RobotOutlined />,
        requiresSuperAdmin: true,
      },
    ],
  },
];

/** Shape ProLayout's `route` prop expects (recursive). */
export interface MenuRoute {
  path: string;
  name: string;
  icon?: ReactNode;
  routes?: MenuRoute[];
}

/**
 * Transform the typed `navigation` config into ProLayout's `{ routes }` shape.
 *
 * - Filters out leaves whose `requiresSuperAdmin` is true when `isSuperAdmin`
 *   is false.
 * - Drops any group left with zero visible children.
 * - Groups get a synthetic `/g/<key>` path so they never collide with a real
 *   route and clicking the parent only expands (no navigation). ProLayout
 *   derives open/selected state from the route tree (parentage), so leaves with
 *   non-hierarchical paths still auto-open their group.
 */
export function buildMenuRoutes(
  isSuperAdmin: boolean,
  nav: NavEntry[] = navigation,
): { routes: MenuRoute[] } {
  const visible = (leaf: NavLeaf) => !leaf.requiresSuperAdmin || isSuperAdmin;

  const routes: MenuRoute[] = [];
  for (const entry of nav) {
    if (isNavGroup(entry)) {
      const children = entry.children.filter(visible);
      if (children.length === 0) continue;
      routes.push({
        path: `/g/${entry.key}`,
        name: entry.name,
        icon: entry.icon,
        routes: children.map((c) => ({ path: c.path, name: c.name, icon: c.icon })),
      });
    } else if (visible(entry)) {
      routes.push({ path: entry.path, name: entry.name, icon: entry.icon });
    }
  }
  return { routes };
}

/**
 * Resolve which real leaf path should be highlighted for a given location.
 *
 * Longest-prefix match over every real leaf path: a leaf is a candidate when
 * the pathname equals it OR starts with it followed by '/'. The longest match
 * wins, so `/admin/brokers/providers` selects "Providers", never the shorter
 * "Overview" (`/admin/brokers`) that is also a prefix. Returns undefined when
 * nothing matches (e.g. an unregistered path).
 *
 * Why this exists: "Overview" (`/admin/brokers`) is the only selectable leaf
 * that is a strict prefix of its siblings. ProLayout's default selection would
 * therefore also light up "Overview" on every broker sub-page. Feeding explicit
 * `selectedKeys` from this resolver removes that ambiguity — it ports the
 * longest-prefix logic the deleted broker `AdminLayout` used, generalized to the
 * whole menu. The `/` boundary check prevents `/signals` from matching
 * `/signals-something`.
 */
export function resolveActiveLeafPath(
  pathname: string,
  nav: NavEntry[] = navigation,
): string | undefined {
  const leafPaths: string[] = [];
  for (const entry of nav) {
    if (isNavGroup(entry)) entry.children.forEach((c) => leafPaths.push(c.path));
    else leafPaths.push(entry.path);
  }

  let best: string | undefined;
  for (const p of leafPaths) {
    if (pathname === p || pathname.startsWith(`${p}/`)) {
      if (best === undefined || p.length > best.length) best = p;
    }
  }
  return best;
}
